import axios from 'axios';
import { performSimpleLogout } from '../utils/logout';
import { preserveBookingState } from '../utils/sessionManager';

console.log('🚀 API SERVICE LOADED - CODE IS UPDATED!');

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('📡 Axios instance created with baseURL:', api.defaults.baseURL);

// Function to clear auth header
export const clearAuthHeader = () => {
  delete api.defaults.headers.common['Authorization'];
};

// Function to set auth header
export const setAuthHeader = (token) => {
  console.log('🔧 setAuthHeader called with token:', token ? `${token.substring(0, 20)}...` : 'null');
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('✅ Authorization header set in axios defaults');
    console.log('📋 Current axios defaults:', api.defaults.headers.common);
  } else {
    clearAuthHeader();
  }
};

// Function to handle authentication errors and logout
const handleAuthError = (error) => {
  // Check if this is a login/register/public request - don't logout for these
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/verify-email',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/setup-password',
    '/cms',
    '/cookie-consent',
    '/analytics',
    '/flights/search',
    '/airports',
    '/countries',
    '/2fa/verify-security-questions',
    '/payments/stripe/success',   // Don't auto-logout on payment callback
    '/payments/paypal/success'    // Don't auto-logout on payment callback
  ];

  const isPublicEndpoint = publicEndpoints.some(endpoint =>
    error.config?.url?.includes(endpoint)
  );

  if (!isPublicEndpoint) {
    // Check if we're in a booking flow - preserve state before logout
    let currentPath = window.location.pathname;
    const currentSearch = window.location.search; // Get query parameters
    const bookingPaths = ['/add-passengers', '/payment', '/booking', '/flight-booking-flow'];
    const isBookingFlow = bookingPaths.some(path => currentPath.includes(path));

    if (isBookingFlow) {
      console.log('🔐 Session expired during booking flow - preserving state');
      
      // Normalize the path - convert /add-passengers to /flight-booking-flow
      if (currentPath.includes('/add-passengers/')) {
        currentPath = currentPath.replace('/add-passengers/', '/flight-booking-flow/');
        console.log('🔄 Normalized path from /add-passengers to /flight-booking-flow:', currentPath);
      }
      
      // Try to extract booking data from URL params
      const searchParams = new URLSearchParams(currentSearch);
      const bookingState = {};
      
      // Preserve all URL parameters
      for (const [key, value] of searchParams.entries()) {
        try {
          // Try to parse JSON values
          bookingState[key] = JSON.parse(value);
        } catch {
          bookingState[key] = value;
        }
      }
      
      // Try to preserve Redux state if available
      try {
        const reduxStateStr = sessionStorage.getItem('persist:root');
        if (reduxStateStr) {
          const reduxState = JSON.parse(reduxStateStr);
          // Store Redux state separately so it can be restored
          bookingState._reduxState = reduxState;
        }
      } catch (error) {
        console.warn('⚠️ Could not preserve Redux state:', error);
      }
      
      // Store the full path with query parameters
      const fullPath = currentPath + currentSearch;
      preserveBookingState(bookingState, fullPath);
      
      console.log('💾 Preserved booking state:', { path: fullPath, data: bookingState });
      
      // Clear auth data but DON'T redirect
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      clearAuthHeader();
      
      // Dispatch custom event to show session expired modal
      // This will be caught by useSessionExpiration hook
      window.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: { preservedState: true }
      }));
      
      // Return true to indicate we handled it (don't do standard logout)
      return true;
    } else {
      // Use the centralized simple logout function for non-booking flows
      performSimpleLogout();
    }
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.url);
    console.log('🔑 Token from sessionStorage:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    console.log('🔑 Authorization header before:', config.headers.Authorization);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Authorization header set in request:', config.headers.Authorization ? 'YES' : 'NO');
    } else {
      console.warn('⚠️ No token found in sessionStorage!');
    }

    // Don't set Content-Type for FormData - let axios/browser handle it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('📤 FormData detected - Content-Type will be set by browser');
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    console.log('📤 Final request headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.url, 'Status:', response.status);
    console.log('📦 Response data:', response.data);
    return response.data;
  },
  (error) => {
    console.error('❌ API Error Response:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message,
      data: error.response?.data
    });

    // Handle authentication errors (401 Unauthorized)
    if (error.response?.status === 401) {
      console.warn('🚨 401 Unauthorized - Triggering logout');
      handleAuthError(error);
    }

    // Handle forbidden errors (403 Forbidden) - also logout
    if (error.response?.status === 403) {
      const message = error.response?.data?.message || '';
      console.warn('🚨 403 Forbidden:', message);
      // If it's a token-related forbidden error, logout
      if (message.toLowerCase().includes('token') ||
        message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('forbidden')) {
        console.warn('🚨 Token-related 403 - Triggering logout');
        handleAuthError(error);
      }
    }

    // Extract the error message from the response
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

    // Create a proper error object that matches what the auth slice expects
    const customError = new Error(errorMessage);
    customError.status = error.response?.status;
    customError.data = error.response?.data;
    customError.isInactive = error.response?.data?.isInactive || false;
    customError.account_status = error.response?.data?.account_status || null;

    return Promise.reject(customError);
  }
);

export default api;